import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { useChannelConnection } from '@/hooks/useChannelConnection';
import { useCreateChannel, useReconnectChannel, useSwitchEngineChannel } from '../../hooks';
import { Icon } from '@/shared/icons'
/**
 * Form for connecting WhatsApp via QR Code (GatewayProvider)
 *
 * Real-time flow using Socket.IO:
 * 1. User fills phone + channel name
 * 2. Click "Generate QR Code" → Creates channel via API
 * 3. Backend calls reconnect → Gateway generates QR Code
 * 4. Frontend receives QR Code via HTTP response
 * 5. Frontend displays QR Code
 * 6. User scans QR Code with WhatsApp
 * 7. Gateway detects connection → Backend emits Socket.IO event
 * 8. Frontend shows "Connected successfully!"
 *
 * @example
 * <GatewayProviderForm
 *   onComplete={(data) => handleAuthComplete(data)}
 *   initialQrCode="data:image/png;base64,..."
 *   channelId="channel-123"
 * />
 */
export interface GatewayProviderFormProps {
  /** Callback when authentication is complete or connection is established */
  onComplete: (data?: GatewayProviderData) => void;
  /** Callback when connection is established via Socket.IO */
  onConnectionEstablished?: () => void;
  /** Initial QR Code from reconnect response */
  initialQrCode?: string;
  /** Initial QR Code expiration timestamp */
  initialQrCodeExpires?: number;
  /** Channel ID (if already created) */
  channelId?: string;
  /** Engine atual (modo edição) — define a engine exibida no card de troca */
  initialEngine?: 'node' | 'go';
  /** Instance key atual (modo edição) — necessário para listener de socket imediato */
  initialInstanceKey?: string;
}

export interface GatewayProviderData {
  providerId: string;
  name: string;
  identifier: string;  // ✅ Número do telefone - única fonte de verdade
  config: { engine?: 'node' | 'go' };
  credentials: Record<string, string>;
}

type ConnectionStatus = 'idle' | 'generating' | 'waiting' | 'connected' | 'error' | 'switching_engine';

export const GatewayProviderForm: React.FC<GatewayProviderFormProps> = ({
  onComplete,
  onConnectionEstablished,
  initialQrCode,
  initialQrCodeExpires,
  channelId: initialChannelId,
  initialEngine,
  initialInstanceKey,
}) => {
  const [phone, setPhone] = useState('');
  const [channelName, setChannelName] = useState('');
  const [engine, setEngine] = useState<'node' | 'go'>(initialEngine ?? 'go');
  // Engine ativa da instância (atualizada após switch bem-sucedido)
  const [activeEngine, setActiveEngine] = useState<'node' | 'go'>(initialEngine ?? 'go');
  // ✅ Se recebeu QR Code inicial, pular para estado 'waiting'
  const [status, setStatus] = useState<ConnectionStatus>(initialQrCode ? 'waiting' : 'idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [instanceKey, setInstanceKey] = useState<string | null>(initialInstanceKey ?? null);
  const [channelId, setChannelId] = useState<string | null>(initialChannelId || null);
  const [qrCodeData, setQrCodeData] = useState<{ qrCode?: string; expires?: number }>({
    qrCode: initialQrCode,
    expires: initialQrCodeExpires
  });
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);

  const createChannel = useCreateChannel();
  const reconnectChannel = useReconnectChannel();
  const switchEngineChannel = useSwitchEngineChannel();

  // ✅ Use real-time Socket.IO connection monitoring
  const connectionStatus = useChannelConnection(instanceKey || undefined);

  // React to Socket.IO events and update QR Code
  useEffect(() => {
    // When QR Code is received via Socket.IO
    if (connectionStatus.qrCode) {
      setQrCodeData({
        qrCode: connectionStatus.qrCode,
        expires: connectionStatus.expires
      });
      if (status === 'generating') {
        setStatus('waiting');
      }
    }

    // When connection is established via Socket.IO
    if (connectionStatus.isConnected && (status === 'waiting' || status === 'generating')) {
      setStatus('connected');

      // Notificar parent component que conexão foi estabelecida
      if (onConnectionEstablished) {
        onConnectionEstablished();
      }
    }

    // When connection error occurs
    if (connectionStatus.error && status !== 'error') {
      console.error('❌ Connection error:', connectionStatus.error);
      setErrorMessage(connectionStatus.error);
      setStatus('error');
    }
  }, [connectionStatus, status]);

  const handleGenerateQR = async () => {
    // ✅ Validation
    if (!phone || !channelName) {
      setErrorMessage('Preencha todos os campos obrigatórios');
      return;
    }

    if (channelName.trim().length < 2) {
      setErrorMessage('Nome do canal deve ter pelo menos 2 caracteres');
      return;
    }

    if (phone.trim().length === 0) {
      setErrorMessage('Número do WhatsApp é obrigatório');
      return;
    }

    setStatus('generating');
    setErrorMessage(null);

    try {
      // ✅ REAL API CALL - Create channel in backend
      // ✅ Backend gerará automaticamente: instanceKey (UUID), instanceToken (UUID)
      // ✅ gatewayUrl vem do process.env.GATEWAY_URL no backend
      // ✅ identifier = phone (única fonte de verdade, sem duplicação)
      const channelRequest = {
        name: channelName.trim(),
        identifier: phone.trim(),  // ✅ Número do telefone - única vez enviado
        providerId: 'gateway-whatsapp',
        config: { engine },  // ✅ Engine selection (node or go)
        credentials: {},
        integrationName: `${channelName.trim()} Integration`,
        assignmentConfig: {
          enabled: true,
          type: 'sequential' as const,
        },
      };

      // Call backend API - Backend returns QR Code directly in HTTP response
      const channel = await createChannel.mutateAsync(channelRequest);

      setChannelId(channel.id);

      // Capturar instanceKey da resposta do backend para usar no Socket.IO
      if (channel.instanceKey) {
        setInstanceKey(channel.instanceKey);
      }

      // Se QR Code veio na resposta HTTP, usar diretamente (não esperar Socket.IO)
      const channelData = channel as any;
      if (channelData.qrCode) {
        setQrCodeData({
          qrCode: channelData.qrCode,
          expires: channelData.qrCodeExpires
        });
        setStatus('waiting');
      } else {
        // Status will automatically change to 'waiting' when Socket.IO receives QR Code
      }

    } catch (error: any) {
      if (error.response?.status === 403) return; // Handled by global interceptor (toast)
      if (error.response?.status === 402) return; // Handled by global interceptor (toast)
      console.error('❌ Error creating channel:', error);
      setErrorMessage(error.response?.data?.message || error.message || 'Erro ao gerar QR Code. Tente novamente.');
      setStatus('error');
    }
  };

  const handleComplete = () => {
    onComplete();
  };

  const handleRetry = () => {
    setStatus('idle');
    setErrorMessage(null);
    setInstanceKey(null);
    setChannelId(null);
    setQrCodeData({});
  };

  const handleReconnect = async () => {
    if (!channelId) {
      console.error('❌ No channel ID available for reconnect');
      return;
    }

    setStatus('generating');
    setErrorMessage(null);

    try {
      const reconnectResult = await reconnectChannel.mutateAsync({ id: channelId });

      if (reconnectResult.qrCode) {
        setQrCodeData({
          qrCode: reconnectResult.qrCode,
          expires: reconnectResult.qrCodeExpires
        });
        setStatus('waiting');
      } else {
        throw new Error('No QR Code in reconnect response');
      }
    } catch (error: any) {
      if (error.response?.status === 403) return; // Handled by global interceptor (toast)
      if (error.response?.status === 402) return; // Handled by global interceptor (toast)
      console.error('❌ Error reconnecting:', error);
      setErrorMessage(error.message || 'Erro ao regenerar QR Code. Tente novamente.');
      setStatus('error');
    }
  };

  const handleSwitchEngineConfirm = async () => {
    if (!channelId) {
      console.error('❌ No channel ID available for engine switch');
      return;
    }

    const targetEngine: 'node' | 'go' = activeEngine === 'node' ? 'go' : 'node';
    setShowSwitchConfirm(false);
    setStatus('switching_engine');
    setErrorMessage(null);

    try {
      const switchResult = await switchEngineChannel.mutateAsync({ channelId, engine: targetEngine });

      if (switchResult.instanceKey && switchResult.instanceKey !== instanceKey) {
        setInstanceKey(switchResult.instanceKey);
      }

      setActiveEngine(targetEngine);

      if (switchResult.qrCode) {
        setQrCodeData({
          qrCode: switchResult.qrCode,
          expires: switchResult.qrCodeExpires
        });
        setStatus('waiting');
      } else {
        // Sem QR na resposta HTTP — aguardar via Socket.IO
        setStatus('generating');
      }

      toast.success(`Engine trocada para ${targetEngine === 'go' ? 'Go' : 'Node'}. Escaneie o novo QR Code.`);
    } catch (error: any) {
      if (error.response?.status === 403) return;
      if (error.response?.status === 402) return;
      const message = error.response?.data?.message || error.message || 'Erro ao trocar engine.';
      console.error('❌ Error switching engine:', error);
      setErrorMessage(message);
      setStatus('error');
      toast.error(message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Step 1: FiPhone and Name Input */}
      {(status === 'idle' || status === 'error') && (
        <div className="space-y-4">
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Nome do Canal *
            </label>
            <Input
              type="text"
              value={channelName}
              onChange={(e) => setChannelName(e.target.value)}
              placeholder="Ex: WhatsApp Suporte"
              className="w-full"
            />
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Número do WhatsApp *
            </label>
            <div className="relative">
              <Icon name="phone" size={18}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10 pointer-events-none"
                style={{ color: 'var(--theme-card-muted)' }} />
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+55 11 99999-9999"
                className="w-full pl-10"
              />
            </div>
            <p
              className="text-xs mt-1"
              style={{ color: 'var(--theme-card-muted)' }}
            >
              Digite o número com código do país (ex: +55 11 99999-9999)
            </p>
          </div>

          <Select
            label="Engine"
            description="Motor de execução do canal"
            options={[
              { value: 'node', label: 'Node' },
              { value: 'go', label: 'GO' },
            ]}
            value={engine}
            onChange={(value) => setEngine(value as 'node' | 'go')}
          />

          {errorMessage && (
            <div
              className="p-3 rounded-lg flex items-center gap-2"
              style={{
                backgroundColor: 'var(--theme-error-background)',
                border: '1px solid var(--theme-error-color)',
              }}
            >
              <Icon name="x-circle" size={16} style={{ color: 'var(--theme-error-color)' }} />
              <p
                className="text-sm"
                style={{ color: 'var(--theme-error-color)' }}
              >
                {errorMessage}
              </p>
            </div>
          )}

          <Button
            onClick={handleGenerateQR}
            className="w-full"
            disabled={createChannel.isPending}
          >
            {createChannel.isPending ? 'Gerando...' : 'Gerar QR Code'}
          </Button>

          {status === 'error' && (
            <Button
              onClick={handleRetry}
              variant="secondary"
              className="w-full"
            >
              Tentar Novamente
            </Button>
          )}
        </div>
      )}

      {/* Switching engine state */}
      {status === 'switching_engine' && (
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center mb-4">
            <Icon name="refresh" size={48}
              className="animate-spin"
              style={{ color: 'var(--theme-button-primary)' }} />
          </div>
          <p
            className="text-lg font-medium"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Trocando engine...
          </p>
          <p
            className="text-sm mt-2"
            style={{ color: 'var(--theme-card-muted)' }}
          >
            Encerrando sessão atual e gerando novo QR Code
          </p>
        </div>
      )}

      {/* Step 2: Generating QR Code (Waiting for Socket.IO event) */}
      {status === 'generating' && (
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center mb-4">
            <Icon name="refresh" size={48}
              className="animate-spin"
              style={{ color: 'var(--theme-button-primary)' }} />
          </div>
          <p
            className="text-lg font-medium"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Gerando QR Code...
          </p>
          <p
            className="text-sm mt-2"
            style={{ color: 'var(--theme-card-muted)' }}
          >
            Aguardando resposta do Gateway
          </p>
        </div>
      )}

      {/* Step 3: Show QR Code - Waiting for connection (HTTP + Socket.IO) */}
      {status === 'waiting' && qrCodeData.qrCode && (
        <div className="text-center space-y-4">
          <div
            className="inline-block p-4 rounded-lg"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              border: '2px solid var(--theme-card-border)',
            }}
          >
            <img
              src={qrCodeData.qrCode}
              alt="QR Code WhatsApp"
              className="w-64 h-64"
            />
          </div>

          <div>
            <p
              className="text-lg font-medium mb-2"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Escaneie o QR Code
            </p>
            <p
              className="text-sm"
              style={{ color: 'var(--theme-card-muted)' }}
            >
              1. Abra o WhatsApp no seu celular<br />
              2. Toque em Mais opções (⋮) &gt; Aparelhos conectados<br />
              3. Toque em Conectar um aparelho<br />
              4. Aponte seu celular para esta tela
            </p>
          </div>

          <div className="flex items-center justify-center gap-2 mt-4">
            <div
              className="w-2 h-2 rounded-full animate-pulse"
              style={{ backgroundColor: 'var(--theme-warning-color)' }}
            />
            <p
              className="text-sm"
              style={{ color: 'var(--theme-card-muted)' }}
            >
              Aguardando conexão...
            </p>
          </div>

          {qrCodeData.expires && (
            <p
              className="text-xs"
              style={{ color: 'var(--theme-card-muted)' }}
            >
              QR Code expira em: {new Date(qrCodeData.expires).toLocaleTimeString()}
            </p>
          )}

          {/* Botão para regenerar QR Code se necessário */}
          <Button
            onClick={handleReconnect}
            variant="secondary"
            className="w-full mt-4"
            disabled={reconnectChannel.isPending}
          >
            {reconnectChannel.isPending ? 'Gerando...' : 'Gerar Novo QR Code'}
          </Button>
        </div>
      )}

      {/* Engine switch card — visível apenas em modo edição (canal já existe) */}
      {channelId && (status === 'idle' || status === 'waiting' || status === 'connected' || status === 'error') && (
        <div
          className="p-4 rounded-lg space-y-3"
          style={{
            backgroundColor: 'var(--theme-card-background)',
            border: '1px solid var(--theme-card-border)',
          }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p
                className="text-sm font-medium"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Engine do canal
              </p>
              <p
                className="text-xs mt-0.5"
                style={{ color: 'var(--theme-card-muted)' }}
              >
                Atual: <strong>{activeEngine === 'go' ? 'Go' : 'Node'}</strong>
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={() => setShowSwitchConfirm(true)}
              disabled={switchEngineChannel.isPending}
            >
              Trocar para {activeEngine === 'go' ? 'Node' : 'Go'}
            </Button>
          </div>
          <p
            className="text-xs"
            style={{ color: 'var(--theme-card-muted)' }}
          >
            Trocar a engine encerra a sessão atual e exige escanear um novo QR Code.
          </p>
        </div>
      )}

      {/* Modal de confirmação de troca de engine (tema claro fixo) */}
      {showSwitchConfirm && createPortal((
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          /* confirmação: NÃO fecha ao clicar fora (DS §Overlays dismissal) */
        >
          <div
            className="w-full max-w-md p-6 rounded-lg space-y-4"
            style={{
              backgroundColor: '#ffffff',
              '--theme-card-text': '#334155',
              '--theme-card-muted': '#94a3b8',
              '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
              '--theme-button-primary': '#8b5cf6',
              '--theme-button-primary-text': '#ffffff',
              '--theme-button-primary-hover': '#7c3aed',
            } as React.CSSProperties}
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h3
                className="text-lg font-semibold"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Trocar engine para {activeEngine === 'go' ? 'Node' : 'Go'}?
              </h3>
              <p
                className="text-sm mt-2"
                style={{ color: 'var(--theme-card-muted)' }}
              >
                A sessão atual deste canal será encerrada e você precisará escanear
                o QR Code novamente para reconectar o WhatsApp.
              </p>
              <p
                className="text-xs mt-2"
                style={{ color: 'var(--theme-card-muted)' }}
              >
                Esta ação não pode ser desfeita automaticamente — para voltar,
                você precisará trocar de novo e escanear o QR mais uma vez.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                variant="secondary"
                onClick={() => setShowSwitchConfirm(false)}
                disabled={switchEngineChannel.isPending}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSwitchEngineConfirm}
                disabled={switchEngineChannel.isPending}
              >
                {switchEngineChannel.isPending ? 'Trocando...' : 'Trocar engine'}
              </Button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* Step 4: Connected Successfully (Detected via Socket.IO) */}
      {status === 'connected' && (
        <div className="text-center py-8">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
            style={{ backgroundColor: 'var(--theme-success-color)' }}
          >
            <Icon name="check-circle" size={32} className="text-white" />
          </div>
          <p
            className="text-lg font-medium mb-2"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Conectado com sucesso!
          </p>
          <p
            className="text-sm mb-6"
            style={{ color: 'var(--theme-card-muted)' }}
          >
            Seu WhatsApp ({phone}) está conectado e pronto para uso.
          </p>
          <Button
            onClick={handleComplete}
            className="w-full"
          >
            Continuar
          </Button>
        </div>
      )}
    </div>
  );
};

GatewayProviderForm.displayName = 'GatewayProviderForm';
